import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import type { JSX } from 'react';

import { ensureCodeQualityEnabled } from '@/features/code-quality/guard.js';
import { WorkspaceContributors } from '@/features/code-quality/WorkspaceContributors.js';

/** The query of the Contributors page: the project whose statistics show, when one was chosen. */
export interface ContributorsSearch {
  project?: string;
}

/** Reads the Contributors query, keeping only a project id that is non-empty text. */
function parseContributorsSearch(
  raw: Record<string, unknown>,
): ContributorsSearch {
  return typeof raw.project === 'string' && raw.project.length > 0
    ? { project: raw.project }
    : {};
}

/**
 * `/contributors`: the git and pull request statistics of a Code Quality project of the workspace
 * selected in the sidebar. `?project=<id>` is the project the picker chose. The guard fails closed
 * with the Code Quality flag.
 */
export const Route = createFileRoute('/contributors')({
  validateSearch: parseContributorsSearch,
  beforeLoad: () => ensureCodeQualityEnabled(),
  component: ContributorsRoute,
});

function ContributorsRoute(): JSX.Element {
  const { project } = useSearch({ from: '/contributors' });
  const navigate = useNavigate();
  return (
    <WorkspaceContributors
      projectId={project}
      onSelectProject={(projectId) =>
        void navigate({
          to: '/contributors',
          search: { project: projectId },
          replace: true,
        })
      }
    />
  );
}
